import { Command, Option } from "clipanion";
import { M8tCommand } from "../lib/m8t-command.js";
import { getAzAccount } from "../lib/auth.js";
import { runAz } from "../lib/az.js";
import { readFoundryConfig, writeFoundryConfig } from "../lib/foundry-config.js";
import { ensureAppReg, patchRedirectUris, resolveAppObjectIdForWrite } from "../lib/app-reg.js";
import {
  resolveRepoRoot,
  ensureResourceGroup,
  resolveFoundryResourceId,
  runBicepDeployment,
  buildBicepParams,
  buildDeployResult,
  resolveAcrPullIdentity,
  resolveAcrResourceId,
  parseFoundryTracingMode,
  parseAssignSubscriptionRoles,
} from "../lib/deploy.js";
import { makeWarningSink } from "../lib/warnings.js";
import { LocalCliError } from "../lib/errors.js";
import { runWhatIf, reportWhatIf, reportWhatIfFailure } from "../lib/whatif.js";
import { classifyWhatIf } from "../lib/whatif-noise.js";
import { colors, renderJson, renderKeyValueBlock, resolveOutputMode } from "../lib/output.js";

// The default points at the public GHCR image's moving `latest` tag (published
// by release-image.yml). After the initial deploy, use `m8t platform update` to
// roll onto a pinned vX.Y.Z — it is the supported update path. The BYOC
// private-ACR flow (--image-ref <acr>) is unchanged.
const DEFAULT_IMAGE_REF = "ghcr.io/m8t-labs/m8t:latest";

export class DeployCommand extends M8tCommand {
  static paths = [["deploy"]];
  static usage = Command.Usage({
    description: "Deploy (or update) the m8t gateway/webapp stack via Bicep.",
    details:
      "Ensures the Entra app reg (or pass --client-id to reuse an existing one — required if you can't create app regs), writes ~/.m8t/config.yaml, ensures the resource group, and runs deploy/main.bicep. The repo is located via ~/.m8t/repo-root.",
  });

  subscription = Option.String("--subscription");
  resourceGroup = Option.String("--resource-group", "rg-m8t-stack");
  location = Option.String("--location", "eastus");
  suffix = Option.String("--suffix", "");
  imageRef = Option.String("--image-ref", DEFAULT_IMAGE_REF);
  // Gateway-only override. Empty ⇒ the gateway uses --image-ref, which is the
  // from-zero case. It exists because the gateway and the voice relay do NOT
  // always run the same image: a converge preserves a BYOC gateway on its own
  // ACR while the relay tracks the release on the public repo. A comparison
  // (`--what-if`) that can only express one image therefore reports the other
  // app as drift on every single run, forever — which is exactly what the
  // infra-drift gate did from 2026-08-11.
  gatewayImageRef = Option.String("--gateway-image-ref", "");
  acrPullIdentity = Option.String("--acrpull-identity");
  acrResourceId = Option.String("--acr-resource-id");
  foundryEndpoint = Option.String("--foundry-endpoint");
  foundryResourceId = Option.String("--foundry-resource-id");
  foundryTracing = Option.String("--foundry-tracing"); // project | account | skip (bicep default: project)
  clientId = Option.String("--client-id");
  whatIf = Option.Boolean("--what-if", false);
  // Only meaningful with --what-if. Routes the comparison through the
  // value-free renderers (see ./lib/whatif-redact.js) instead of the default
  // before/after renderer. Defaults false so a local, interactive run keeps
  // showing values — that is the whole diagnostic point of --what-if.
  // Automation that forwards this output anywhere non-private (a CI log, an
  // issue) MUST pass --redact.
  redact = Option.Boolean("--redact", false);
  output = Option.String("--output");

  // Subscription-scoped role assignments. Omitted ⇒ the template default (true).
  // Pass false when deploying as a principal scoped to the resource group only:
  // it cannot deploy at subscription scope, and those assignments persist
  // idempotently from the initial deployment anyway.
  assignSubscriptionRoles = Option.String("--assign-subscription-roles");

  /**
   * The installer image the updater Container-Apps Job runs.
   *
   * main.bicep only provisions the updater when an image is supplied
   * (`provisionUpdaterEffective = provisionUpdater && !empty(installerImage)`),
   * so a run that omits this describes a deployment WITHOUT an updater. For a
   * real deploy that is merely a skip; for a `--what-if` comparison it is worse
   * than that — the comparison proposes REMOVING an updater job that exists and
   * should, and reports it as drift on every single run.
   */
  installerImage = Option.String("--installer-image");

  // Librarian Codify. Empty audience/allowlist keeps the route dormant.
  brainCodifyAudience = Option.String("--brain-codify-audience");
  brainCodifyAllowlist = Option.String("--brain-codify-allowlist");
  brainCodifyWorkflowRef = Option.String("--brain-codify-workflow-ref");
  brainCodifyModel = Option.String("--brain-codify-model");
  brainCodifyEvalModel = Option.String("--brain-codify-eval-model");

  // Referee — all optional, undefined by default ⇒ the bicep defaults
  // apply (dormant: gatewayCpu=0.25, gatewayMemory=0.5Gi, every referee/exam var
  // empty). Only pass these when explicitly enabling the referee exam stack.
  gatewayCpu = Option.String("--gateway-cpu");
  gatewayMemory = Option.String("--gateway-memory");
  refereeEnabled = Option.String("--referee-enabled");
  refereeBrainRepos = Option.String("--referee-brain-repos");
  refereeFeedRepo = Option.String("--referee-feed-repo");
  refereeInstallationId = Option.String("--referee-installation-id");
  refereeWebhookHmacKvUri = Option.String("--referee-webhook-hmac-kv-uri");
  examKvUri = Option.String("--exam-kv-uri");
  examLaWorkspaceId = Option.String("--exam-la-workspace-id");
  brainEvalDeployment = Option.String("--brain-eval-deployment");
  brainAppLogin = Option.String("--brain-app-login");
  refereeCheckpointDir = Option.String("--referee-checkpoint-dir");
  examApiBase = Option.String("--exam-api-base");

  async executeCommand(): Promise<number> {
    const mode = resolveOutputMode(
      this.output as "pretty" | "json" | "auto" | undefined,
      this.context.stdout as NodeJS.WriteStream,
    );
    const log = (msg: string) => {
      if (mode !== "json") this.context.stdout.write(msg + "\n");
    };
    // Advisories go through `log` too (it's a no-op in json mode, same as for
    // progress lines) - the sink is what makes them ALSO land in the JSON result.
    // Styling is applied here, in the echo callback, NOT baked into the stored
    // string: `warnings.list()` is the machine-readable `warnings` array on
    // `--output json` (a bash installer parses it into a customer-facing status
    // doc), and picocolors decides color support once at module-import time from
    // the real process - not per call, and not from this stream. Raw escape
    // codes would reach that JSON under CI (CI=1), FORCE_COLOR, or native
    // Windows (which forces color unconditionally). Keep the payload plain;
    // dim only what's echoed for a human. Mirrors coder/deploy.ts and
    // azure-exec/deploy.ts.
    const warnings = makeWarningSink((m) => { log(colors.dim(m)); });

    if (this.whatIf) {
      try {
        // Strictly read-only: NO app-reg create, NO config write, NO RG create.
        //
        // Everything the comparison depends on — including account
        // resolution and endpoint resolution — lives INSIDE this try. Those
        // two calls shell out (`az account show`, and reading/validating
        // config), so a transient failure there (throttling, a token not yet
        // usable immediately after login, ...) is exactly as much "the
        // comparison did not run to completion" as a failure inside the
        // what-if call itself. Left outside the try, a throw here would
        // escape to the generic error renderer and exit 1 — a code the
        // workflow reads as "ran, found differences" — for a run in which no
        // comparison was ever attempted.
        if (this.subscription) {
          await runAz(["account", "set", "--subscription", this.subscription]);
        }
        const account = await getAzAccount();
        const foundryEndpoint = this.foundryEndpoint ?? (await readFoundryConfig())?.projectEndpoint;
        if (!foundryEndpoint) {
          throw new LocalCliError({
            code: "DEPLOY_NO_FOUNDRY_ENDPOINT",
            message: "No Foundry endpoint provided.",
            hint: "Pass --foundry-endpoint <url> or set projectEndpoint in ~/.m8t/config.yaml (m8t config set / m8t switch).",
          });
        }
        const clientId = this.clientId ?? (await readFoundryConfig())?.clientId;
        if (!clientId) {
          throw new LocalCliError({
            code: "DEPLOY_WHATIF_NO_CLIENT",
            message: "--what-if needs a client id but none was provided or configured.",
            hint: "Pass --client-id <appId>, or run from a host with ~/.m8t/config.yaml.",
          });
        }
        const foundryResourceId = await resolveFoundryResourceId(foundryEndpoint, this.foundryResourceId);
        const repoRoot = await resolveRepoRoot();
        const acrPullIdentityResourceId = resolveAcrPullIdentity({ explicit: this.acrPullIdentity });
        const acrResourceId = acrPullIdentityResourceId ? "" : await resolveAcrResourceId({ imageRef: this.imageRef, explicit: this.acrResourceId });
        const params = buildBicepParams({
          location: this.location, suffix: this.suffix, imageRef: this.imageRef,
          ...(this.gatewayImageRef ? { gatewayImageRef: this.gatewayImageRef } : {}),
          tenantId: account.tenantId, clientId,
          foundryResourceId, foundryProjectEndpoint: foundryEndpoint,
          acrPullIdentityResourceId, acrResourceId,
          foundryTracingMode: parseFoundryTracingMode(this.foundryTracing),
          gatewayCpu: this.gatewayCpu,
          gatewayMemory: this.gatewayMemory,
          refereeEnabled: this.refereeEnabled,
          refereeBrainRepos: this.refereeBrainRepos,
          refereeFeedRepo: this.refereeFeedRepo,
          refereeInstallationId: this.refereeInstallationId,
          refereeWebhookHmacKvUri: this.refereeWebhookHmacKvUri,
          examKvUri: this.examKvUri,
          examLaWorkspaceId: this.examLaWorkspaceId,
          brainEvalDeployment: this.brainEvalDeployment,
          brainAppLogin: this.brainAppLogin,
          refereeCheckpointDir: this.refereeCheckpointDir,
          examApiBase: this.examApiBase,
          brainCodifyAudience: this.brainCodifyAudience,
          brainCodifyAllowlist: this.brainCodifyAllowlist,
          brainCodifyWorkflowRef: this.brainCodifyWorkflowRef,
          brainCodifyModel: this.brainCodifyModel,
          brainCodifyEvalModel: this.brainCodifyEvalModel,
          ...(parseAssignSubscriptionRoles(this.assignSubscriptionRoles) === undefined
            ? {}
            : { assignSubscriptionRoles: parseAssignSubscriptionRoles(this.assignSubscriptionRoles) }),
          ...(this.installerImage ? { installerImage: this.installerImage } : {}),
        });
        const changes = await runWhatIf({ resourceGroup: this.resourceGroup, repoRoot, params });
        const classified = classifyWhatIf(changes);
        const { output, exitCode, diagnostic } = reportWhatIf(classified, { json: mode === "json", redact: this.redact });
        this.context.stdout.write(output + "\n");
        // stdout is the machine-readable result and must stay parseable, so the
        // value-free per-finding detail goes to stderr. Without it a redacted
        // JSON run reports a COUNT and nothing else, which is how the drift gate
        // came to publish "11 unexpected" next to a run holding no detail at all.
        if (diagnostic) this.context.stderr.write(diagnostic + "\n");
        return exitCode;
      } catch (e) {
        // The comparison did not run to completion — a resolve step or the
        // `az` what-if call itself threw before a result could be produced.
        // This must never exit with reportWhatIf's own 0 (clean) or 1 (drift
        // found): a caller has to be able to tell "nothing to report" apart
        // from "we don't know" from the exit code alone, never from output
        // shape (see WHATIF_RUN_FAILED_EXIT_CODE). With --redact set, the
        // rendered message must not reproduce the underlying command line or
        // its parameters — handled here, at the comparison's own code path,
        // so the guarantee holds no matter who invokes this command.
        const { output, exitCode } = reportWhatIfFailure(e, { json: mode === "json", redact: this.redact });
        this.context.stdout.write(output + "\n");
        return exitCode;
      }
    }

    // Prereqs (apply path — the --what-if path resolves its own copy above,
    // inside its try, so a failure here can be reported as a drift-comparison
    // failure rather than escaping to this path's generic error exit).
    // Set subscription FIRST (so the account below reflects the target
    // tenant/sub), then read the account, then resolve the foundry endpoint.
    if (this.subscription) {
      await runAz(["account", "set", "--subscription", this.subscription]);
    }
    const account = await getAzAccount();
    const foundryEndpoint = this.foundryEndpoint ?? (await readFoundryConfig())?.projectEndpoint;
    if (!foundryEndpoint) {
      throw new LocalCliError({
        code: "DEPLOY_NO_FOUNDRY_ENDPOINT",
        message: "No Foundry endpoint provided.",
        hint: "Pass --foundry-endpoint <url> or set projectEndpoint in ~/.m8t/config.yaml (m8t config set / m8t switch).",
      });
    }

    // 1. App reg (BYO or managed).
    log(this.clientId ? `using existing app reg ${this.clientId}` : "ensuring Entra app reg…");
    const app = await ensureAppReg({ tenantId: account.tenantId, clientId: this.clientId });
    if (app.appObjectId === null && this.clientId) {
      // ensureAppReg's BYO short-circuit (opts.clientId branch) makes ZERO
      // Graph calls, so it never runs ensureExposeAnApi the way the managed
      // path does right after create. Redirect URIs are NOT part of this gap
      // any more: resolveAppObjectIdForWrite + patchRedirectUris below write
      // those for a supplied --client-id too, and step 5 raises its own
      // warning when that write itself fails. Only Expose-an-API (the
      // identifierUris + user_impersonation scope + Azure CLI pre-auth that
      // `az account get-access-token --scope api://<clientId>/.default`
      // needs) is still silently skipped on this path.
      warnings.add(
        "BYO app reg - Expose-an-API was not verified or configured (Graph writes are skipped for a supplied " +
        "--client-id); if sign-in fails, configure identifierUris + a user_impersonation scope + Azure CLI " +
        "pre-authorization per deploy/operator-app-registration-setup.md.",
      );
    }

    // 2. Write config.yaml (Track 2 lib).
    await writeFoundryConfig({ tenantId: app.tenantId, clientId: app.clientId, projectEndpoint: foundryEndpoint });

    // 3. Resource group.
    log(`ensuring resource group ${this.resourceGroup}…`);
    await ensureResourceGroup(this.resourceGroup, this.location);

    // 4. Bicep.
    const foundryResourceId = await resolveFoundryResourceId(foundryEndpoint, this.foundryResourceId);
    const repoRoot = await resolveRepoRoot();
    const acrPullIdentityResourceId = resolveAcrPullIdentity({ explicit: this.acrPullIdentity });
    // Skip ACR resolution on the reference path — a pure-reference deploy must
    // not hit DEPLOY_ACR_NOT_FOUND from an unresolvable host label.
    const acrResourceId = acrPullIdentityResourceId
      ? ""
      : await resolveAcrResourceId({ imageRef: this.imageRef, explicit: this.acrResourceId });
    if (acrPullIdentityResourceId) {
      log(`using AcrPull identity ${acrPullIdentityResourceId.split("/").pop() ?? acrPullIdentityResourceId}`);
    } else if (acrResourceId) {
      log(`provisioning AcrPull identity for ${acrResourceId.split("/").pop() ?? acrResourceId}…`);
    }
    const params = buildBicepParams({
      location: this.location,
      suffix: this.suffix,
      imageRef: this.imageRef,
      ...(this.gatewayImageRef ? { gatewayImageRef: this.gatewayImageRef } : {}),
      tenantId: app.tenantId,
      clientId: app.clientId,
      foundryResourceId,
      foundryProjectEndpoint: foundryEndpoint,
      acrPullIdentityResourceId,
      acrResourceId,
      foundryTracingMode: parseFoundryTracingMode(this.foundryTracing),
      gatewayCpu: this.gatewayCpu,
      gatewayMemory: this.gatewayMemory,
      refereeEnabled: this.refereeEnabled,
      refereeBrainRepos: this.refereeBrainRepos,
      refereeFeedRepo: this.refereeFeedRepo,
      refereeInstallationId: this.refereeInstallationId,
      refereeWebhookHmacKvUri: this.refereeWebhookHmacKvUri,
      examKvUri: this.examKvUri,
      examLaWorkspaceId: this.examLaWorkspaceId,
      brainEvalDeployment: this.brainEvalDeployment,
      brainAppLogin: this.brainAppLogin,
      refereeCheckpointDir: this.refereeCheckpointDir,
      examApiBase: this.examApiBase,
      brainCodifyAudience: this.brainCodifyAudience,
      brainCodifyAllowlist: this.brainCodifyAllowlist,
      brainCodifyWorkflowRef: this.brainCodifyWorkflowRef,
      brainCodifyModel: this.brainCodifyModel,
      brainCodifyEvalModel: this.brainCodifyEvalModel,
      ...(parseAssignSubscriptionRoles(this.assignSubscriptionRoles) === undefined
        ? {}
        : { assignSubscriptionRoles: parseAssignSubscriptionRoles(this.assignSubscriptionRoles) }),
      ...(this.installerImage ? { installerImage: this.installerImage } : {}),
    });
    log("running Bicep deployment (~3-5 min)…");
    const outputs = await runBicepDeployment({
      resourceGroup: this.resourceGroup,
      repoRoot,
      params,
      deploymentName: `m8t-deploy-${account.subscriptionId.slice(0, 8)}`,
    });

    // 5. Redirect URIs. Sign-in fails with AADSTS50011 until the gateway's own
    //    FQDN is registered, so this runs for a supplied --client-id too and
    //    not only for an app reg this command created. Whether we may write is
    //    the directory's call, not something a flag can tell us.
    const writableAppObjectId = await resolveAppObjectIdForWrite({
      appObjectId: app.appObjectId,
      clientId: this.clientId,
    });
    if (writableAppObjectId) {
      await patchRedirectUris(writableAppObjectId, outputs.containerAppFqdn);
    } else {
      // Only reachable when the directory refused the read. Names the exact
      // value to add, because at this point we know the FQDN — the advice this
      // replaces was printed before the FQDN existed and so could not. Routed
      // through warnings (not a bare log()) so it survives --output json —
      // the installer's automated path is the one nobody is watching, and
      // this was recorded as a known, accepted gap (762f452f) until now.
      // Plain text — see the warnings-sink construction above for why.
      warnings.add(
        `could not register the sign-in redirect URI automatically — add https://${outputs.containerAppFqdn} ` +
        `as an SPA redirect URI on app registration ${app.clientId}, or sign-in will fail with AADSTS50011`,
      );
    }

    const webappUrl = `https://${outputs.containerAppFqdn}`;
    if (mode === "json") {
      this.context.stdout.write(
        renderJson(buildDeployResult({
          webappUrl, resourceGroup: this.resourceGroup, clientId: app.clientId,
          resourceNames: outputs.resourceNames, warnings: warnings.list(),
        })) + "\n",
      );
      return 0;
    }
    this.context.stdout.write(
      renderKeyValueBlock([
        { key: "webapp", value: webappUrl },
        { key: "resource group", value: this.resourceGroup },
        { key: "client id", value: app.clientId },
        { key: "container app", value: outputs.resourceNames.containerApp ?? "-" },
      ]) + "\n",
    );
    this.context.stdout.write(colors.dim(`next: 'm8t open' to sign in to the webapp.\n`));
    return 0;
  }
}
